import usePublicReportData from './usePublicReportData';
import styles from './PublicReportData.module.css';
import { SkeletonizedValue } from '@round/ui-kit';
import HeaderSummary from 'Modules/Plans/components/HeaderSummary/HeaderSummary';
import RunDates from 'Modules/Plans/components/RunDates/RunDates';
import moment from 'moment';
import CircleBadge from 'Modules/Plans/components/CircleBadge/CircleBadge';
import { Image } from '@round/ui-kit';
import { assets } from '@round/whitelabel-ui';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { asMoneyWithoutZeros } from 'helpers';

const { LogoShort: RoundLogoShort } = assets;

const PublicReportData = () => {
    const {
        data,
        status,
        currencyId,
        team: { data: team, status: teamsStatus },
    } = usePublicReportData();
    const { getCurrencyById } = useCurrencies();
    const isInitialized = status === 'success' || status === 'error';
    const isTeamInitialized = teamsStatus === 'success' || teamsStatus === 'error';

    const runDatesDuration =
        data?.first_post_date && data?.last_post_date
            ? moment.duration(moment(data.last_post_date).diff(moment(data.first_post_date))).asDays()
            : null;

    const runDatesDurationDisplayValue = runDatesDuration
        ? `${runDatesDuration.toFixed()} day${runDatesDuration === 1 ? '' : 's'}`
        : '-';

    const totalCost = asMoneyWithoutZeros(data?.total_cost, getCurrencyById(currencyId));

    return (
        <>
            <div className={styles.goBackContainer}>
                <div className={styles.team}>
                    <CircleBadge>
                        <Image loading={!isTeamInitialized} className={styles.teamLogo} src={team?.logo || ''} />
                    </CircleBadge>
                    {team?.name}
                </div>
                <RoundLogoShort className={styles.roundLogo} />
            </div>
            <div className={styles.reportInfoContainer}>
                <Image
                    loading={!isInitialized}
                    skeletonCircle={false}
                    className={styles.reportImage}
                    src={data?.image ?? ''}
                    alt="report image"
                />

                <div className={styles.reportDetails}>
                    <div>
                        <h1 className={styles.title}>
                            <SkeletonizedValue isInitialized={isInitialized}>{data?.name || '-'}</SkeletonizedValue>
                        </h1>
                        <p className={styles.description}>
                            <SkeletonizedValue isInitialized={isInitialized}>
                                {data?.description || '-'}
                            </SkeletonizedValue>
                        </p>
                    </div>

                    <HeaderSummary>
                        <HeaderSummary.Item>
                            <HeaderSummary.Label>Run Dates</HeaderSummary.Label>
                            <HeaderSummary.Value className={styles.runDatesContainer} isLoading={!isInitialized}>
                                {data?.first_post_date && data?.last_post_date ? (
                                    <>
                                        <RunDates
                                            startDate={moment(data.first_post_date).format('DD MMM YYYY')}
                                            endDate={moment(data.last_post_date).format('DD MMM YYYY')}
                                        />
                                        {typeof runDatesDuration === 'number' && runDatesDuration > 0 && (
                                            <span className={styles.duration}>{runDatesDurationDisplayValue}</span>
                                        )}
                                    </>
                                ) : (
                                    '-'
                                )}
                            </HeaderSummary.Value>
                        </HeaderSummary.Item>

                        {typeof data?.total_cost === 'number' && (
                            <HeaderSummary.Item>
                                <HeaderSummary.Label>Total cost</HeaderSummary.Label>
                                <HeaderSummary.Value>{totalCost}</HeaderSummary.Value>
                            </HeaderSummary.Item>
                        )}
                    </HeaderSummary>
                </div>
            </div>
        </>
    );
};

export default PublicReportData;
